<template>
  <v-container fluid>
    <v-row>
      <v-col cols="10">
        <v-btn color="primary" class="mb-4" dark tile @click="createPost">
          新規投稿作成
        </v-btn>
        <v-form @submit.prevent="searchPosts">
          <v-layout row>
            <v-text-field
              v-model="searchQuery.idEq"
              label="記事ID"
              style="max-width: 100px"
              type="number"
              clearable
              class="ma-2"
            />
            <v-text-field
              v-model="searchQuery.keyword"
              label="キーワード"
              style="max-width: 300px"
              clearable
              class="ma-2"
            />
            <v-text-field
              v-model="searchQuery.userNameCont"
              label="作成者"
              style="max-width: 100px"
              clearable
              class="ma-2"
            />
            <v-text-field
              v-model="searchQuery.writerableNameCont"
              label="ライター"
              style="max-width: 100px"
              clearable
              class="ma-2"
            />
            <v-text-field
              v-model="searchQuery.slugEq"
              label="パーマリンク"
              style="max-width: 150px"
              clearable
              class="ma-2"
            />
            <v-select
              v-model="searchQuery.statusObject"
              :items="selectableStatuses"
              item-text="type"
              style="max-width: 110px"
              class="ma-2"
              item-value="value"
              clearable
              return-object
              label="ステータス"
            />
            <v-select
              v-model="searchQuery.postTypeEq"
              :items="selectablePostTypes"
              item-value="value"
              style="max-width: 110px"
              class="ma-2"
              clearable
              label="記事種別"
            />
            <div class="ma-2">
              <SelectableTags v-model="tags" label="タグ" />
            </div>
            <div class="text-center">
              <v-btn
                x-large
                color="primary"
                dark
                rounded
                class="px-10 my-2"
                type="submit"
                @click="searchPosts"
                @submit.prevent="searchPosts"
              >
                検索
              </v-btn>
            </div>
          </v-layout>
        </v-form>

        <AdminPostTable
          :posts="posts"
          @delete-post="deletePost"
          @post-download-url="postDownloadUrl"
          @post-duplicate="postDuplicate"
        />

        <infinite-loading
          spinner="spiral"
          class="mt-2"
          :identifier="infiniteId"
          @infinite="infiniteHandler"
        >
          <template v-slot:no-more>
            <v-chip label outlined> 全て表示されました。 </v-chip>
          </template>
          <template v-slot:no-results>
            <v-chip label outlined> 表示できるものがありません。 </v-chip>
          </template>
        </infinite-loading>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import axios from 'axios'
import InfiniteLoading from 'vue-infinite-loading'
import Qs from 'qs'
import { mapState } from 'vuex'
import { deserialize } from 'deserialize-json-api'
import AdminPostTable from '@/components/commons/AdminPostTable'
import headUtilsMixin from '@/mixins/head_utils'
import blobDownloader from '@/mixins/blob_downloader'
import SelectableTags from './SelectableTags'
import SitePermissionUtils, {
  PRODUCTS_SITUATION_ID
} from '@/mixins/site_permission_utils'

export default {
  name: 'Posts',
  components: {
    InfiniteLoading,
    AdminPostTable,
    SelectableTags
  },
  mixins: [headUtilsMixin, SitePermissionUtils, blobDownloader],
  data: () => {
    return {
      pageTitle: '記事を書く',
      posts: [],
      page: 1,
      searchQuery: {
        keyword: '',
        advancedStatusQuery: null,
        compositeStatusEq: null,
        idEq: '',
        postTitleCont: '',
        slugEq: '',
        statusObject: null,
        statusEq: '',
        recentOrderStatusEq: '',
        writerableNameCont: '',
        tagsIdEqAny: [],
        postTypeEq: '',
        userNameCont: '',
        userIdEq: ''
      },
      selectableStatuses: [
        { type: '未応募', value: 0, model: 'order' },
        { type: '執筆中', value: 10, model: 'order' },
        { type: '検収中', value: 30, model: 'order' },
        { type: '公開', value: 1001, model: 'post' },
        { type: '非公開', value: 1002, model: 'post' },
        { type: '下書き', value: 1000, model: 'post' }
      ],
      orderStatusColors: {
        not_applied: '',
        writing: 'primary',
        under_inspection: 'warning',
        completed: 'success'
      },
      infiniteId: +new Date(),
      tags: []
    }
  },
  computed: {
    ...mapState({
      site: (state) => state.SiteStore.site,
      siteExtensionPermissions: (state) =>
        state.SiteStore.siteExtensionPermissions,
      currentUser: (state) => state.UserStore.currentUser
    }),
    selectablePostTypes: function () {
      let selectablePostTypes = [{ text: '通常記事', value: 0 }]
      if (this.isPermitted(PRODUCTS_SITUATION_ID)) {
        selectablePostTypes.push({
          text: '商品記事',
          value: 1
        })
      }
      return selectablePostTypes
    }
  },
  watch: {
    tags () {
      this.searchQuery.tagsIdEqAny = this.tags.map((tag) => tag.id)
    }
  },
  methods: {
    searchPosts () {
      this.searchQuery.statusEq = ''
      this.searchQuery.recentOrderStatusEq = ''
      this.page = 1
      this.posts = []
      this.infiniteId++
      this.searchParamsKeyChange()
    },
    searchParamsKeyChange () {
      if (!this.searchQuery.statusObject) {
        return true
      }

      if (this.searchQuery.statusObject.model === 'order') {
        this.searchQuery.recentOrderStatusEq = this.searchQuery.statusObject.value
      } else {
        this.searchQuery.statusEq = this.searchQuery.statusObject.value - 1000
      }
    },
    infiniteHandler ($state) {
      if (!Object.values(this.currentUser.permittedSituations).includes('all_post_management')) {
        // userIdEqを空で送信することで全ての記事を取得できる
        this.searchQuery.userIdEq = this.currentUser.id
      }
      axios
        .get('/api/posts', {
          params: {
            page: this.page,
            q: this.searchQuery
          },
          paramsSerializer: function (params) {
            return Qs.stringify(params, { arrayFormat: 'brackets' })
          }
        })
        .then(({ data }) => {
          const fetchedPosts = deserialize(data).data

          if (fetchedPosts.length > 0) {
            this.page++
            this.posts = this.posts.concat(fetchedPosts)
            $state.loaded()
          } else {
            $state.complete()
          }
        })
    },
    createPost () {
      this.$store.dispatch('PostEditor/createPost')
    },
    deletePost (postId) {
      this.$store.dispatch('LoaderStore/displayLoader')
      axios
        .delete(`/api/posts/${postId}`)
        .then(
          () => {
            const index = this.posts.findIndex((v) => v.id === postId)
            this.posts.splice(index, 1)
            const payload = {
              status: 200,
              message: '記事を削除しました'
            }
            this.$store.dispatch('SystemMessageStore/createMessage', payload)
          },
          (error) => {
            const payload = {
              status: error.response.status,
              message: error.response.data.messages
            }
            this.$store.dispatch('SystemMessageStore/createMessage', payload)
          }
        )
        .finally(() => {
          this.$store.dispatch('LoaderStore/hideLoader')
        })
    },
    postDownloadUrl (postId) {
      axios
        .get(`/api/html_export/${postId}`, { responseType: 'blob' })
        .then(
          (response) => {
            this.downloadBlob(response)
          },
          (error) => {
            const payload = {
              status: error.response.status,
              message: error.response.data.messages
            }
            this.$store.dispatch('SystemMessageStore/createMessage', payload)
          }
        )
        .finally(() => {
          this.$store.dispatch('LoaderStore/hideLoader')
        })
    },
    postDuplicate (postId) {
      axios.post(`/api/posts/${postId}/duplicates`).then(
        () => {
          const payload = {
            status: 200,
            message: '複製しました。'
          }
          this.$store.dispatch('SystemMessageStore/createMessage', payload)
          this.searchPosts()
        },
        () => {
          const payload = {
            status: 404,
            message: '複製に失敗しました。'
          }
          this.$store.dispatch('SystemMessageStore/createMessage', payload)
        }
      )
    }
  }
}
</script>

<style scoped></style>
